import { Stack } from "@mantine/core"
import { useForm, zodResolver } from "@mantine/form"
import { Button, ErrorBox } from "@tfg/ui"
import type { AxiosError } from "axios"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import { standardEmail } from "@tfg/types/schemas/utils"

import { LoginTextInput } from "~/pages/Auth/components/LoginTextInput"

import { LoginButton } from "./LoginButton"

const passwordSchema = z.object({
  username: standardEmail(),
  password: z.string().min(1, "Password is required"),
})

type PasswordFormProps = {
  onSubmit: (values: z.infer<typeof passwordSchema>) => Promise<void>
  onForgotPassword: () => void
  isLoading: boolean
  error: Error | AxiosError<unknown, unknown> | null
  username: string
}

export const PasswordForm = ({
  onSubmit,
  onForgotPassword,
  isLoading,
  error,
  username,
}: PasswordFormProps) => {
  const { t } = useTranslation("account")
  const form = useForm<z.infer<typeof passwordSchema>>({
    validate: zodResolver(passwordSchema),
    initialValues: {
      username,
      password: "",
    },
  })

  return (
    <>
      <ErrorBox error={error} />
      <Stack align="right" w="100%" gap="var(--tfg-molecule-padding-y)">
        <LoginTextInput
          name="password"
          type="password"
          autoComplete="current-password"
          required
          autoFocus
          label={t("login.labels.password")}
          placeholder={t("login.placeholders.password")}
          onSubmit={() => form.onSubmit(onSubmit)()}
          {...form.getInputProps("password")}
        />
        <Button
          w="fit-content"
          style={{ alignSelf: "end", textAlign: "right", justifySelf: "end" }}
          size="xs"
          variant="tertiary"
          fz="xs"
          onClick={onForgotPassword}
        >
          {t("login.forgotPassword.button")}
        </Button>
      </Stack>
      <LoginButton
        loading={isLoading}
        onClick={() => form.onSubmit(onSubmit)()}
      >
        {t("login.submit")}
      </LoginButton>
    </>
  )
}
